import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { database } from "./firebase";

/* 🔐 AUTH & COMMON PAGES */
import LoginSelect from "./LoginSelect";
import Login from "./Login";
import CustomerView from "./CustomerView";

/* 👑 ALI ADMIN PAGE */
import AliAdmin from "./AliAdmin";

/* 🧾 MAIN SYSTEM PAGES */
import BillingSystem from "./BillingSystem";
import OwnerAddUser from "./OwnerAddUser";
import ProductionPage from "./ProductionPage";
import RawStockPage from "./RawStockPage";
import ScrapStockPage from "./ScrapStockPage";
import TokenHistoryPage from "./TokenHistoryPage";
import BossPage from "./BossPage";
import CustomerSalesInsights from "./CustomerSalesInsights";
import SalemanPage from "./SalemanPage";
import SalemanReport from "./SalemanReport";
import CustomerDetail from "./CustomerDetail";
import AllCustomer from "./AllCustomer";
import Report from "./Report";
import MySales from "./MySales";

/* ✅ Extra pages */
import BottlersEntry from "./BottlersEntry";
import ManagePrices from "./ManagePrices";
import Enterprises from "./Enterprises";
import OpeningStock from "./OpeningStock";
import ClosingStock from "./ClosingStock";
import BankPage from "./BankPage";
import BottlersSP from "./BottlersSP";
import EnterprisesSP from "./EnterprisesSP";
import Expenses from "./Expenses";
import AttendancePage from "./AttendancePage";
import AddEmployee from "./AddEmployee";
import EmployeeList from './EmployeeList';
import AttendanceReport from "./AttendanceReport";
import ManualEntryPage from "./ManualEntryPage";

export default function App() {
  const [user, setUser] = useState(null);

  /* 🔒 SITE OPEN / CLOSE STATE */
  const [siteOpen, setSiteOpen] = useState(true);
  const [closeMessage, setCloseMessage] = useState("");

  /* 🔄 READ SITE STATUS FROM FIREBASE */
  useEffect(() => {
    const statusRef = ref(database, "siteStatus");
    onValue(statusRef, (snap) => {
      if (snap.exists()) {
        setSiteOpen(snap.val().isOpen);
        setCloseMessage(snap.val().message || "");
      }
    });
  }, []);

  /* 🔒 DISABLE RIGHT CLICK / INSPECT */
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key.toUpperCase())) ||
        (e.ctrlKey && e.key.toUpperCase() === "U")
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /* 🔓 ALLOWED PAGES EVEN WHEN SITE IS CLOSED */
  const currentPath = window.location.pathname;
  const isAliPage = currentPath === "/ali";

  /* 🚫 GLOBAL WEBSITE CLOSED PAGE */
  if (!siteOpen && !isAliPage) {
    return (
      <div
        style={{
          height: "100vh",
          backgroundColor: "#ffe6f0",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: 20,
        }}
      >
        <h1 style={{ color: "#ff1493" }}>Jauhari</h1>

        <div
          style={{
            background: "#fff",
            border: "3px solid #ff1493",
            padding: 25,
            borderRadius: 10,
            maxWidth: 420,
          }}
        >
          🚫 <strong>Website Closed</strong>
          <br /><br />
          {closeMessage || "Website is temporarily unavailable."}
        </div>
      </div>
    );
  }

  /* 🔓 NOT LOGGED IN */
  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<LoginSelect />} />
          <Route path="/login" element={<Login onLogin={setUser} />} />
          <Route path="/customer" element={<CustomerView />} />

          {/* 👑 ALI ADMIN PAGE */}
          <Route path="/ali" element={<AliAdmin />} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    );
  }

  /* ✅ LOGGED IN USERS */
  return (
    <Router>
      <div style={{ padding: 20 }}>
        <div style={{ marginBottom: 20 }}>
          <p>
            Logged in as <strong>{user.name}</strong> ({user.role})
          </p>
          <button onClick={() => setUser(null)}>Logout</button>
        </div>

        <Routes>
          {/* 👑 ALI ADMIN PAGE (ALWAYS ACCESSIBLE) */}
          <Route path="/ali" element={<AliAdmin />} />

          {/* OWNER + BOSS (FULL ACCESS) */}
          {/* BOSS */}
          {user.role === "boss" && (
            <>
              <Route path="/" element={<BossPage phone={user.phone} />} />
              <Route
                path="/staff"
                element={<BillingSystem role="staff" phone={user.phone} />}
              />
              <Route
                path="/owner"
                element={
                  <BillingSystem
                    role="boss"
                    phone={user.phone}
                    redirectedFrom="boss"
                  />
                }
              />
              <Route path="/owner-add-user" element={<OwnerAddUser />} />
              <Route path="/raw-stock" element={<RawStockPage role="boss" />} />
              <Route path="/scrap" element={<ScrapStockPage role="boss" />} />
              <Route
                path="/production"
                element={<ProductionPage role="boss" phone={user.phone} />}
              />
              <Route
                path="/customer-sales"
                element={<CustomerSalesInsights />}
              />
              <Route path="/saleman-report" element={<SalemanReport />} />
              <Route path="/customer-detail" element={<CustomerDetail />} />
              <Route path="/all-customer" element={<AllCustomer />} />

              {/* ✅ New Bottlers System Pages */}
              <Route path="/bottlers" element={<BottlersEntry />} />
              <Route path="/manage-prices" element={<ManagePrices />} />
              <Route path="/enterprises" element={<Enterprises />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/add-employee" element={<AddEmployee />} />
              <Route path="/employees" element={<EmployeeList />} />
               <Route path="/attendance-report" element={<AttendanceReport />} />
               <Route path="/manual-entry" element={<ManualEntryPage />} />
               <Route
                path="/opening-stock"
                element={
                  <OpeningStock role="boss" phone={user.phone} />
                }
              />



              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
          {/* ⭐ SPECIAL MANAGER */}
{user.role === "special manager" && (
  <>
    {/* Default landing */}
    <Route path="/" element={<Navigate to="/bank" />} />

    <Route path="/bank" element={<BankPage />} />
    <Route path="/bottlersSP" element={<BottlersSP />} />
    <Route path="/enterprisesSP" element={<EnterprisesSP />} />
    <Route path="/expenses" element={<Expenses />} />
    <Route path="/attendance" element={<AttendancePage />} />

    <Route path="*" element={<Navigate to="/bank" />} />
  </>
)}


          {/* PRODUCTION */}
          {user.role === "production" && (
            <>
              <Route
                path="/"
                element={
                  <ProductionPage role="production" phone={user.phone} />
                }
              />
              <Route
                path="/staff"
                element={
                  <BillingSystem role="production" phone={user.phone} />
                }
              />
              <Route
                path="/raw-stock"
                element={<RawStockPage role="production" />}
              />
              <Route
                path="/scrap"
                element={<ScrapStockPage role="production" />}
              />
              <Route
                path="/token-history"
                element={<TokenHistoryPage role="production" />}
              />

              {/* ✅ Opening / Closing Stock for Production */}
              <Route
                path="/opening-stock"
                element={
                  <OpeningStock role="production" phone={user.phone} />
                }
              />
              <Route
                path="/closing-stock"
                element={
                  <ClosingStock role="production" phone={user.phone} />
                }
              />

              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}

          {/* STOCK MANAGER */}
          {user.role === "stock manager" && (
            <>
              <Route
                path="/"
                element={<RawStockPage role="stock manager" />}
              />
              <Route
                path="/scrap"
                element={<ScrapStockPage role="stock manager" />}
              />
              <Route
                path="/staff"
                element={<BillingSystem role="staff" phone={user.phone} />}
              />
              <Route
                path="/production"
                element={
                  <ProductionPage role="stock manager" phone={user.phone} />
                }
              />

              {/* ✅ Opening / Closing Stock for Stock Manager (optional but useful) */}
              <Route
                path="/opening-stock"
                element={
                  <OpeningStock role="stock manager" phone={user.phone} />
                }
              />
              <Route
                path="/closing-stock"
                element={
                  <ClosingStock role="stock manager" phone={user.phone} />
                }
              />

              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}

          {/* STAFF & MANAGER */}
          {["staff", "manager"].includes(user.role) && (
            <>
              <Route
                path="/"
                element={
                  <BillingSystem role={user.role} phone={user.phone} />
                }
              />
              <Route
                path="/staff"
                element={
                  <BillingSystem role={user.role} phone={user.phone} />
                }
              />
              <Route
                path="/raw-stock"
                element={<RawStockPage role={user.role} />}
              />
              <Route path="/scrap" element={<ScrapStockPage role={user.role} />} />
              <Route
                path="/production"
                element={
                  <ProductionPage role={user.role} phone={user.phone} />
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}

          {/* SALESMAN */}
          {user.role === "salesman" && (
            <>
              <Route
                path="/"
                element={<SalemanPage role={user.role} phone={user.phone} />}
              />
              <Route
                path="/saleman"
                element={<SalemanPage role={user.role} phone={user.phone} />}
              />
              <Route path="/report" element={<Report />} />
              <Route path="/my-sales" element={<MySales />} />
              <Route path="*" element={<Navigate to="/saleman" />} />
            </>
          )}

// 🧑‍💼 HR ROLE (LIMITED ACCESS)
{user.role === "hr" && (
  <>
    {/* Default page */}
    <Route path="/" element={<Navigate to="/attendance" />} />

    {/* Allowed pages */}
     <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/add-employee" element={<AddEmployee />} />
              <Route path="/employees" element={<EmployeeList />} />
               <Route path="/attendance-report" element={<AttendanceReport />} />
               <Route path="/manual-entry" element={<ManualEntryPage />} />

    {/* ❌ Block everything else */}
    <Route path="*" element={<Navigate to="/attendance" />} />
  </>
)}
          {/* CUSTOMER (still accessible when logged in) */}
          <Route path="/customer" element={<CustomerView />} />
        </Routes>
      </div>
    </Router>
  );
}
